/**
 * ExerciseViewer — Smart wrapper that uses 3D avatar when available,
 * falls back to SVG ExerciseAnimation otherwise.
 *
 * Setup guide for 3D mode:
 *
 * 1. Create your avatar:
 *    → Go to https://readyplayer.me
 *    → Upload the reference photo
 *    → Download the .glb file
 *    → Place it at: /public/models/avatar.glb
 *
 * 2. Get exercise animations:
 *    → Go to https://www.mixamo.com (free Adobe account)
 *    → Upload your avatar.glb (or use their default character)
 *    → Search for animations: "squat", "push up", "jumping jacks", etc.
 *    → Download each as .fbx "Without Skin" (animation only)
 *    → Convert to .glb using https://gltf.report/ or Blender
 *    → Place in: /public/models/anims/squat.glb, pushup.glb, etc.
 *
 * 3. That's it! This component auto-detects the files and switches to 3D.
 */

import React, { useState, useEffect, lazy, Suspense } from 'react';
import { ExerciseAnimation } from './ExerciseAnimation';
import { getAnimationUrl, DEFAULT_AVATAR_URL, checkAnimationExists } from '../data/animations3d';
import { getMocapRecording } from './ExerciseRecorder';

// Lazy-load 3D viewers (heavy: Three.js + WebGL)
const AvatarViewer3D = lazy(() =>
  import('./AvatarViewer3D').then(mod => ({ default: mod.AvatarViewer3D }))
);
const MocapPlayer = lazy(() =>
  import('./MocapPlayer').then(mod => ({ default: mod.MocapPlayer }))
);

// Cache: track which assets are available
const assetCache = new Map();

async function isAssetAvailable(url) {
  if (assetCache.has(url)) return assetCache.get(url);
  const available = await checkAnimationExists(url);
  assetCache.set(url, available);
  return available;
}

export function ExerciseViewer({
  exerciseId,
  className = '',
  height = 120,
  paused = false,
  speed = 1,
  showControls = false,
}) {
  const [mode, setMode] = useState('svg'); // 'svg' | 'mocap' | '3d' | 'checking'
  const [animUrl, setAnimUrl] = useState(null);
  const [mocapData, setMocapData] = useState(null);

  // Check available animation sources: mocap recording > GLB > SVG
  useEffect(() => {
    let cancelled = false;

    async function check() {
      setMode('checking');

      // Priority 1: Check for mocap recording (localStorage/Firestore)
      const recording = getMocapRecording(exerciseId);
      if (recording && recording.frames?.length > 5) {
        setMocapData(recording);
        setMode('mocap');
        return;
      }

      // Priority 2: Check for GLB avatar + animation
      const avatarReady = await isAssetAvailable(DEFAULT_AVATAR_URL);
      if (!avatarReady || cancelled) {
        setMode('svg');
        return;
      }

      const aUrl = getAnimationUrl(exerciseId);
      const animReady = await isAssetAvailable(aUrl);
      if (cancelled) return;

      if (animReady) {
        setAnimUrl(aUrl);
      }
      setMode('3d');
    }

    check();
    return () => { cancelled = true; };
  }, [exerciseId]);

  // SVG fallback
  if (mode === 'svg' || mode === 'checking') {
    return <ExerciseAnimation exerciseId={exerciseId} className={className} />;
  }

  // Mocap recording mode — play recorded movement as 3D skeleton
  if (mode === 'mocap' && mocapData) {
    return (
      <Suspense fallback={<ExerciseAnimation exerciseId={exerciseId} className={className} />}>
        <MocapPlayer
          mocapData={mocapData}
          height={height}
          paused={paused}
          speed={speed}
          className={className}
        />
      </Suspense>
    );
  }

  // GLB 3D model mode
  return (
    <Suspense fallback={<ExerciseAnimation exerciseId={exerciseId} className={className} />}>
      <AvatarViewer3D
        avatarUrl={DEFAULT_AVATAR_URL}
        animationUrl={animUrl}
        height={height}
        paused={paused}
        speed={speed}
        className={className}
        showControls={showControls}
        cameraPosition={[0, 0.9, 4.5]}
      />
    </Suspense>
  );
}
